import React, { useRef } from "react";
import { TableRowData } from "./data-table";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import { MovementActivity } from "../lib/api";
import { format } from "date-fns";

const BLANK = "_______________________________";
const BLANK_ITEM_ROWS = 15;

interface DetailsOfReceivedDrawerProps {
  movementData: MovementActivity | undefined;
  detailsOfReceived: TableRowData | null;
  blank?: boolean;
  onClose: () => void;
}

const DetailsOfReceivedDrawer: React.FC<DetailsOfReceivedDrawerProps> = ({
  movementData,
  detailsOfReceived,
  blank = false,
  onClose,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const items = movementData?.items ?? [];

  const sourceName = blank
    ? BLANK
    : movementData?.sourceSite
      ? [
          movementData.sourceSite.customer?.fullName,
          movementData.sourceSite.name,
        ]
          .filter(Boolean)
          .join(" - ")
      : movementData?.sourceWarehouse?.name ||
        movementData?.sourceVendor?.fullName ||
        "N/A";

  const destinationName = blank
    ? BLANK
    : movementData?.destinationSite
      ? [
          movementData.destinationSite.customer?.fullName,
          movementData.destinationSite.name,
        ]
          .filter(Boolean)
          .join(" - ")
      : movementData?.destinationWarehouse?.name ||
        movementData?.destinationCustomer?.fullName ||
        "N/A";

  const docNo = blank
    ? BLANK
    : movementData?.reference
      ? movementData.reference
      : "N/A";

  const offloadingArea = blank ? BLANK : destinationName;

  const driverName = blank
    ? BLANK
    : (movementData?.driverName ?? movementData?.driver_name ?? BLANK);
  const driverPhone = blank
    ? BLANK
    : (movementData?.driverPhoneNumber ??
      movementData?.driver_phone_number ??
      BLANK);
  const vehicleNo = blank
    ? BLANK
    : (movementData?.vehicleNumber ?? movementData?.vehicle_number ?? BLANK);

  const displayDate = blank
    ? BLANK
    : movementData?.plannedDate
      ? format(new Date(movementData.plannedDate), "MMM dd, yyyy")
      : "_______________________________";

  const checkedBy = blank
    ? BLANK
    : (movementData?.approvedBy ??
      movementData?.approved_by ??
      "_________________________");

  const receivedBy = blank
    ? BLANK
    : (movementData?.receivedBy ??
      movementData?.received_by ??
      "________________________________________");

  const approvedBy = blank
    ? BLANK
    : (movementData?.approvedBy ??
      movementData?.approved_by ??
      "________________________________________");

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title = blank
      ? "Blank Details of Received"
      : `Details of Received ${sourceName} → ${destinationName}`;
    window.print();

    document.title = originalTitle;
    if (parent) parent.insertBefore(el, next);
    else el.remove();
  };

  return (
    <SidePanel
      open={!!detailsOfReceived}
      onClose={onClose}
      width="w-[900px]"
      layer="overlay"
    >
      {detailsOfReceived && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={
              blank
                ? "Blank Details of Received"
                : `Details of Received for ${destinationName}`
            }
            onClose={onClose}
            actions={[
              {
                label: "Print",
                icon: Icons.Print,
                onClick: handlePrint,
                pinned: true,
              },
            ]}
          />

          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={
                  isMobile ? "w-full p-3 bg-gray-50" : "w-full p-6 bg-gray-50"
                }
              >
                <ScaledPreview>
                  <div
                    ref={printRef}
                    className="print-area p-4 bg-white shadow-lg rounded-lg"
                  >
                    {/* Header */}
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-950 mb-2 border-b-2 border-gray-950 inline-block mx-auto">
                        DETAILS OF RECEIVED
                      </h1>
                    </div>
                    <p className="text-gray-950 text-right">No: {docNo}</p>
                    <div className="text-center mb-4 border-b-2 border-gray-950 pb-2">
                      <img
                        src="/images/logo.png"
                        alt="Logo"
                        className="h-12 mx-auto mb-2"
                      />
                      <p className="text-sm text-gray-950">
                        Dealers in scaffolding & formwork (sale, rental and
                        fixing, cradle & scissor lift rental and sale)
                      </p>
                      <p className="text-sm text-gray-950">
                        Office: Plot 161, Block 161, Wazo, Tegeta
                      </p>
                      <p className="text-sm text-gray-950">
                        P.O Box 35854, Dar es Salaam, Tanzania. Sales:
                        +255(0)780 973 724 Warehouse: +255(0)780 973 728
                      </p>
                      <p className="text-sm text-gray-950">
                        Email: info@waposcaffolding.com Website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>

                    {/* Meta */}
                    <div className="mt-4 grid grid-cols-2 gap-6 text-sm text-gray-950">
                      <div className="space-y-1">
                        <p>
                          <span className="font-semibold">Source:</span>{" "}
                          <span className="font-medium">{sourceName}</span>
                        </p>
                        <p>
                          <span className="font-semibold">Destination:</span>{" "}
                          <span className="font-medium">{destinationName}</span>
                        </p>
                        <p>
                          <span className="font-semibold">Invoice No:</span>{" "}
                          _______________________________
                        </p>
                        <p>
                          <span className="font-semibold">Driver Name:</span>{" "}
                          {driverName}
                        </p>
                        <p>
                          <span className="font-semibold">
                            Driver Phone No:
                          </span>{" "}
                          {driverPhone}
                        </p>
                        <p>
                          <span className="font-semibold">Vehicle No:</span>{" "}
                          {vehicleNo}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p>
                          <span className="font-semibold">Date:</span>{" "}
                          {displayDate}
                        </p>
                        <p>
                          <span className="font-semibold">Offloaded by:</span>{" "}
                          ________________________
                        </p>
                        <p>
                          <span className="font-semibold">Checked by:</span>{" "}
                          {checkedBy}
                        </p>
                      </div>
                    </div>

                    {/* Items table */}
                    <div className="mt-5 overflow-x-auto">
                      <table className="w-full border border-gray-950 text-xs">
                        <thead>
                          <tr className="bg-gray-950">
                            <th className="text-white p-2 text-left">
                              Description
                            </th>
                            <th className="text-white p-2 text-center">Qty</th>
                            <th className="text-white p-2 text-center">Unit</th>
                            <th className="text-white p-2 text-center">Good</th>
                            <th className="text-white p-2 text-center">
                              Broken
                            </th>
                            <th className="text-white p-2 text-center">
                              Discrepancy
                            </th>
                            <th className="text-white p-2 text-center">
                              Offloading Area
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {blank ? (
                            Array.from({ length: BLANK_ITEM_ROWS }).map(
                              (_, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                </tr>
                              ),
                            )
                          ) : items.length > 0 ? (
                            items.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {[
                                    item.inventory?.description || "N/A",
                                    item.inventory?.length === "undefined" ||
                                    item.inventory?.length == null
                                      ? "---"
                                      : item.inventory.length,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {item.totalQuantity ?? 0}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  Pc.
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {item.goodQuantity ?? 0}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {item.damagedQuantity ?? 0}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  -
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {offloadingArea}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={7}
                                className="border border-gray-950 px-2 py-8 text-center text-gray-950"
                              >
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Footer signatures */}
                    <div className="mt-8 grid grid-cols-2 gap-6 text-sm text-gray-950">
                      <div className="space-y-2">
                        <p className="font-semibold">Received By:</p>
                        <p>{receivedBy}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="font-semibold">Approved By:</p>
                        <p>{approvedBy}</p>
                      </div>
                    </div>
                  </div>
                </ScaledPreview>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default DetailsOfReceivedDrawer;
